<script setup>
import { computed, onMounted, onActivated, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import FlowsAPI from 'dashboard/api/flows';
import AccountAPI from 'dashboard/api/account';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { updateAccount } = useAccount();

const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const flows = ref([]);
const loading = ref(true);
const settingsLoading = ref(false);
const savingTimezone = ref(false);
const currentTimezone = ref('');
const selectedTimezone = ref('');

const timezones = [
  { value: 'America/La_Paz', label: 'Bolivia (La Paz) - UTC-4' },
  {
    value: 'America/Argentina/Buenos_Aires',
    label: 'Argentina (Buenos Aires) - UTC-3',
  },
  { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo) - UTC-3' },
  { value: 'America/Santiago', label: 'Chile (Santiago) - UTC-3/UTC-4' },
  { value: 'America/Bogota', label: 'Colombia (Bogotá) - UTC-5' },
  { value: 'America/Guayaquil', label: 'Ecuador (Guayaquil) - UTC-5' },
  { value: 'America/Lima', label: 'Perú (Lima) - UTC-5' },
  { value: 'America/Caracas', label: 'Venezuela (Caracas) - UTC-4' },
  {
    value: 'America/Mexico_City',
    label: 'México (Ciudad de México) - UTC-6',
  },
  { value: 'America/Panama', label: 'Panamá - UTC-5' },
  { value: 'America/Costa_Rica', label: 'Costa Rica - UTC-6' },
  { value: 'America/Montevideo', label: 'Uruguay (Montevideo) - UTC-3' },
  {
    value: 'America/Asuncion',
    label: 'Paraguay (Asunción) - UTC-4/UTC-3',
  },
  { value: 'Europe/Madrid', label: 'España (Madrid) - UTC+1/UTC+2' },
  { value: 'Europe/London', label: 'Reino Unido (Londres) - UTC+0/UTC+1' },
  {
    value: 'America/New_York',
    label: 'Estados Unidos (Nueva York) - UTC-5/UTC-4',
  },
  {
    value: 'America/Los_Angeles',
    label: 'Estados Unidos (Los Ángeles) - UTC-8/UTC-7',
  },
  { value: 'UTC', label: 'UTC (Tiempo Universal Coordinado)' },
];

const fetchAccountSettings = async () => {
  settingsLoading.value = true;
  try {
    const timezone = await AccountAPI.getTimezone();
    currentTimezone.value = timezone || 'UTC';
    selectedTimezone.value = currentTimezone.value;
  } catch (error) {
    useAlert(t('FLOW_BUILDER.SETTINGS.ERROR.FETCH_FAILED'));
  } finally {
    settingsLoading.value = false;
  }
};

const saveTimezone = async () => {
  savingTimezone.value = true;
  try {
    await updateAccount({
      reporting_timezone: selectedTimezone.value,
    });

    const timezone = await AccountAPI.getTimezone();
    currentTimezone.value = timezone || 'UTC';
    selectedTimezone.value = currentTimezone.value;

    useAlert(t('FLOW_BUILDER.SETTINGS.SUCCESS.TIMEZONE_UPDATED'));
  } catch (error) {
    useAlert(t('FLOW_BUILDER.SETTINGS.ERROR.UPDATE_FAILED'));
  } finally {
    savingTimezone.value = false;
  }
};
const showDeleteConfirm = ref(false);
const showTimezoneModal = ref(false);
const selectedFlow = ref(null);
const deletingFlow = ref(false);
const searchQuery = ref('');
const filterTrigger = ref('');
const filterStatus = ref('');

const accountId = computed(() => getters.getCurrentAccountId.value);

const triggerOptions = computed(() => {
  const events = new Set();
  flows.value.forEach(f => {
    const def = f.flow_definition;
    if (def?.nodes) {
      def.nodes.forEach(n => {
        if (n.type === 'trigger' && n.data?.event_name) {
          events.add(n.data.event_name);
        }
      });
    }
  });
  return [
    { value: '', label: t('FLOW_BUILDER.LIST.FILTER_ALL_TRIGGERS') },
    ...Array.from(events).map(e => ({
      value: e,
      label:
        t(`FLOW_BUILDER.NODE_EDITOR.TRIGGER.EVENTS.${e.toUpperCase()}`) || e,
    })),
  ];
});

const statusOptions = computed(() => [
  { value: '', label: t('FLOW_BUILDER.LIST.FILTER_ALL_STATUSES') },
  { value: 'active', label: t('FLOW_BUILDER.LIST.STATUS_LABELS.ACTIVE') },
  { value: 'paused', label: t('FLOW_BUILDER.LIST.STATUS_LABELS.PAUSED') },
  { value: 'draft', label: t('FLOW_BUILDER.LIST.STATUS_LABELS.DRAFT') },
]);

const filteredFlows = computed(() => {
  let result = flows.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(f => f.name?.toLowerCase().includes(q));
  }
  if (filterTrigger.value) {
    result = result.filter(f => {
      const def = f.flow_definition;
      if (!def?.nodes) return false;
      return def.nodes.some(
        n => n.type === 'trigger' && n.data?.event_name === filterTrigger.value
      );
    });
  }
  if (filterStatus.value) {
    result = result.filter(f => f.status === filterStatus.value);
  }
  return result;
});

const loadFlows = async () => {
  loading.value = true;
  try {
    const response = await FlowsAPI.get();
    flows.value = response.data;
  } catch (error) {
    useAlert(t('FLOW_BUILDER.LIST.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const createFlow = () => {
  router.push({
    name: 'flow_builder_new',
    params: { accountId: accountId.value },
  });
};

const editFlow = flow => {
  router.push({
    name: 'flow_builder_edit',
    params: { accountId: accountId.value, flowId: flow.id },
  });
};

const cloneFlow = async flow => {
  try {
    await FlowsAPI.clone(flow.id);
    useAlert(t('FLOW_BUILDER.LIST.CLONE_SUCCESS'));
    loadFlows();
  } catch (error) {
    useAlert(t('FLOW_BUILDER.LIST.CLONE_ERROR'));
  }
};

const toggleFlowStatus = async flow => {
  try {
    if (flow.status === 'active') {
      await FlowsAPI.deactivate(flow.id);
      flow.status = 'paused';
    } else {
      await FlowsAPI.activate(flow.id);
      flow.status = 'active';
    }
    loadFlows();
  } catch (error) {
    useAlert(t('FLOW_BUILDER.LIST.STATUS_ERROR'));
  }
};

const openDeleteConfirm = flow => {
  selectedFlow.value = flow;
  showDeleteConfirm.value = true;
};

const confirmDelete = async () => {
  if (!selectedFlow.value) return;
  deletingFlow.value = true;
  try {
    await FlowsAPI.delete(selectedFlow.value.id);
    useAlert(t('FLOW_BUILDER.LIST.DELETE_SUCCESS'));
    showDeleteConfirm.value = false;
    loadFlows();
  } catch (error) {
    useAlert(t('FLOW_BUILDER.LIST.DELETE_ERROR'));
  } finally {
    deletingFlow.value = false;
  }
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadgeClass = status => {
  switch (status) {
    case 'active':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'paused':
      return 'bg-n-amber-3 text-n-amber-11';
    case 'draft':
      return 'bg-n-slate-3 text-n-slate-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
};

const statusDotClass = status => {
  switch (status) {
    case 'active':
      return 'bg-n-teal-9';
    case 'paused':
      return 'bg-n-amber-9';
    case 'draft':
      return 'bg-n-slate-8';
    default:
      return 'bg-n-slate-8';
  }
};

const statusLabel = status => {
  switch (status) {
    case 'active':
      return t('FLOW_BUILDER.LIST.STATUS_LABELS.ACTIVE');
    case 'paused':
      return t('FLOW_BUILDER.LIST.STATUS_LABELS.PAUSED');
    case 'draft':
      return t('FLOW_BUILDER.LIST.STATUS_LABELS.DRAFT');
    default:
      return status;
  }
};

const getTriggerInfo = flow => {
  const def = flow.flow_definition;
  if (!def?.nodes) return null;
  const trigger = def.nodes.find(n => n.type === 'trigger');
  if (!trigger) return null;
  const eventName = trigger.data?.event_name;
  const inboxName = trigger.data?.inbox_name;
  const translatedEvent = eventName
    ? t(`FLOW_BUILDER.NODE_EDITOR.TRIGGER.EVENTS.${eventName.toUpperCase()}`)
    : '';
  if (translatedEvent && inboxName) {
    return `${translatedEvent} #${inboxName}`;
  }
  return translatedEvent || eventName || '';
};

const getInboxInfo = flow => {
  const def = flow.flow_definition;
  if (!def?.nodes) return null;
  const trigger = def.nodes.find(n => n.type === 'trigger');
  if (!trigger) return null;
  const inboxId = trigger.data?.inbox_id;
  const inboxName = trigger.data?.inbox_name;
  let inbox = null;
  if (inboxId) {
    inbox = inboxes.value.find(i => i.id === inboxId);
  }
  if (!inbox && inboxName) {
    inbox = inboxes.value.find(i => i.name === inboxName);
  }
  if (!inbox) return null;
  return {
    name: inbox.name,
    channelType: inbox.channel_type,
    isWhatsapp: inbox.channel_type === 'Channel::Whatsapp',
    isOfficialApi: inbox.provider === 'whatsapp_cloud',
    provider: inbox.provider || '',
    inbox,
  };
};

onMounted(() => {
  store.dispatch('inboxes/get');
  loadFlows();
  fetchAccountSettings();
});

onActivated(() => {
  loadFlows();
  fetchAccountSettings();
});
</script>

<template>
  <div class="flex flex-col w-full">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('FLOW_BUILDER.LIST.HEADER_TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-n-weak text-n-slate-10 hover:text-n-blue-10 hover:bg-n-alpha-2 transition-colors"
          :title="t('FLOW_BUILDER.SETTINGS.TIMEZONE.TITLE')"
          @click="showTimezoneModal = true"
        >
          <span class="i-lucide-globe h-5 w-5" />
        </button>
        <Button
          solid
          blue
          icon="i-lucide-plus"
          :label="t('FLOW_BUILDER.LIST.CREATE_BUTTON')"
          size="sm"
          @click="createFlow"
        />
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-12 text-sm text-n-slate-10"
    >
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
      {{ t('FLOW_BUILDER.LIST.TITLE') }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!flows.length"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <div
        class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center mb-4"
      >
        <span class="i-lucide-workflow w-8 h-8 text-n-slate-9" />
      </div>
      <p class="text-sm text-n-slate-10 mb-4">
        {{ t('FLOW_BUILDER.LIST.EMPTY') }}
      </p>
      <Button
        solid
        blue
        icon="i-lucide-plus"
        :label="t('FLOW_BUILDER.LIST.CREATE_BUTTON')"
        size="sm"
        @click="createFlow"
      />
    </div>

    <template v-else>
      <!-- Filters -->
      <div class="flex items-center gap-2 mb-4">
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="t('FLOW_BUILDER.LIST.SEARCH_PLACEHOLDER')"
          class="flex-1 h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 border-none outline outline-1 outline-n-weak dark:outline-n-weak focus:outline-n-brand dark:focus:outline-n-brand transition-all"
        />
        <select
          v-model="filterTrigger"
          class="flex-1 h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border-none outline outline-1 outline-n-weak dark:outline-n-weak focus:outline-n-brand cursor-pointer whitespace-nowrap"
        >
          <option
            v-for="opt in triggerOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <select
          v-model="filterStatus"
          class="flex-1 h-9 px-3 text-sm rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 border-none outline outline-1 outline-n-weak dark:outline-n-weak focus:outline-n-brand cursor-pointer whitespace-nowrap"
        >
          <option
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <!-- No results after filtering -->
      <div
        v-if="!filteredFlows.length"
        class="flex items-center justify-center py-12 text-sm text-n-slate-10"
      >
        {{ t('FLOW_BUILDER.LIST.EMPTY') }}
      </div>

      <!-- Flow cards -->
      <div v-else class="grid gap-4">
        <div
          v-for="flow in filteredFlows"
          :key="flow.id"
          class="border border-n-slate-6 rounded-xl p-4 hover:border-n-slate-8 transition-colors bg-n-surface-1 cursor-pointer"
          @click="editFlow(flow)"
        >
          <div class="flex items-start justify-between gap-4">
            <!-- Left: Info -->
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-n-slate-12 truncate mb-1">
                {{ flow.name }}
              </h3>
              <p
                v-if="flow.description"
                class="text-xs text-n-slate-10 line-clamp-2 mb-1"
              >
                {{ flow.description }}
              </p>
              <p
                v-if="getTriggerInfo(flow)"
                class="text-xs text-n-slate-10 truncate mb-2"
              >
                {{ getTriggerInfo(flow) }}
              </p>

              <!-- Tags row -->
              <div class="flex flex-wrap items-center gap-2 mt-2">
                <!-- Channel badge -->
                <span
                  v-if="getInboxInfo(flow)"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="
                    getInboxInfo(flow)?.isWhatsapp &&
                    !getInboxInfo(flow)?.isOfficialApi
                      ? 'bg-n-teal-9 text-white'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  <ChannelIcon
                    :inbox="getInboxInfo(flow)?.inbox"
                    use-brand-icon
                    class="w-4 h-4"
                  />
                  {{
                    getInboxInfo(flow)?.isOfficialApi
                      ? 'API'
                      : getInboxInfo(flow)?.name
                  }}
                </span>

                <!-- Status badge -->
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="statusBadgeClass(flow.status)"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="statusDotClass(flow.status)"
                  />
                  {{ statusLabel(flow.status) }}
                </span>

                <!-- Executions badge -->
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-blue-3 text-n-blue-11"
                >
                  <span class="i-lucide-zap w-3 h-3" />
                  {{ flow.executions_count ?? 0 }}
                </span>

                <!-- Updated date -->
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-slate-3 text-n-slate-10"
                >
                  <span class="i-lucide-clock w-3 h-3" />
                  {{ formatDate(flow.updated_at) }}
                </span>
              </div>
            </div>

            <!-- Right: Actions -->
            <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
                :title="t('FLOW_BUILDER.LIST.EDIT')"
                @click="editFlow(flow)"
              >
                <span class="i-lucide-pencil w-4 h-4" />
              </button>
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
                :title="t('FLOW_BUILDER.LIST.CLONE')"
                @click="cloneFlow(flow)"
              >
                <span class="i-lucide-copy w-4 h-4" />
              </button>
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
                :title="
                  flow.status === 'active'
                    ? t('FLOW_BUILDER.LIST.PAUSE')
                    : t('FLOW_BUILDER.LIST.ACTIVATE')
                "
                @click="toggleFlowStatus(flow)"
              >
                <span class="i-lucide-power w-4 h-4" />
              </button>
              <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:text-n-ruby-9 hover:bg-n-ruby-3 transition-colors"
                :title="t('FLOW_BUILDER.LIST.DELETE')"
                @click="openDeleteConfirm(flow)"
              >
                <span class="i-lucide-trash-2 w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Timezone Modal -->
    <Teleport to="body">
      <div
        v-if="showTimezoneModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        @click.self="showTimezoneModal = false"
      >
        <div
          class="bg-n-slate-1 rounded-xl shadow-2xl p-6 w-full max-w-lg mx-4"
        >
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('FLOW_BUILDER.SETTINGS.TITLE') }}
            </h3>
            <button
              class="p-1 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
              @click="showTimezoneModal = false"
            >
              <span class="i-lucide-x h-5 w-5" />
            </button>
          </div>

          <div v-if="settingsLoading" class="flex items-center gap-2 py-4">
            <span
              class="i-lucide-loader-2 h-5 w-5 animate-spin text-n-slate-10"
            />
            <span class="text-sm text-n-slate-10">{{
              t('FLOW_BUILDER.SETTINGS.LOADING')
            }}</span>
          </div>

          <div v-else class="space-y-4">
            <p class="text-sm text-n-slate-10">
              {{ t('FLOW_BUILDER.SETTINGS.DESCRIPTION') }}
            </p>

            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-11">
                {{ t('FLOW_BUILDER.SETTINGS.TIMEZONE.LABEL') }}
              </label>
              <select
                v-model="selectedTimezone"
                class="w-full h-10 rounded-lg border border-n-slate-6 bg-n-surface-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="tz in timezones"
                  :key="tz.value"
                  :value="tz.value"
                >
                  {{ tz.label }}
                </option>
              </select>
              <p class="mt-1 text-sm text-n-slate-10">
                {{ t('FLOW_BUILDER.SETTINGS.TIMEZONE.HELP') }}
              </p>
            </div>

            <div
              v-if="currentTimezone"
              class="rounded-lg bg-n-slate-3 p-3 dark:bg-n-slate-9"
            >
              <p class="text-sm text-n-slate-11">
                <span class="font-medium">
                  {{ t('FLOW_BUILDER.SETTINGS.TIMEZONE.CURRENT') }}
                </span>
                {{
                  timezones.find(tz => tz.value === currentTimezone)?.label ||
                  currentTimezone
                }}
              </p>
            </div>

            <div
              class="rounded-lg border border-n-amber-3 bg-n-amber-2 p-4 dark:border-n-amber-8 dark:bg-n-amber-9"
            >
              <div class="flex gap-3">
                <span
                  class="i-lucide-info h-5 w-5 shrink-0 text-n-amber-10 dark:text-n-amber-4"
                />
                <div>
                  <h3
                    class="mb-1 text-sm font-semibold text-n-amber-11 dark:text-n-amber-1"
                  >
                    {{ t('FLOW_BUILDER.SETTINGS.INFO.TITLE') }}
                  </h3>
                  <p class="text-sm text-n-amber-10 dark:text-n-amber-2">
                    {{ t('FLOW_BUILDER.SETTINGS.INFO.MESSAGE') }}
                  </p>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="t('FLOW_BUILDER.EDITOR.CANCEL')"
                @click="showTimezoneModal = false"
              />
              <Button
                solid
                blue
                size="sm"
                :label="
                  savingTimezone
                    ? t('FLOW_BUILDER.SETTINGS.SAVING')
                    : t('FLOW_BUILDER.SETTINGS.SAVE')
                "
                :disabled="
                  savingTimezone || selectedTimezone === currentTimezone
                "
                @click="saveTimezone"
              />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <woot-delete-modal
      v-if="showDeleteConfirm"
      v-model:show="showDeleteConfirm"
      :on-confirm="confirmDelete"
      :title="t('FLOW_BUILDER.LIST.DELETE')"
      :message="
        t('FLOW_BUILDER.LIST.DELETE_CONFIRM', { name: selectedFlow?.name })
      "
      :confirm-text="t('FLOW_BUILDER.LIST.DELETE')"
      :reject-text="t('FLOW_BUILDER.EDITOR.CANCEL')"
    />
  </div>
</template>
